"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/lang";
import { Logo } from "@/components/logo";

export default function CCPAPage() {
  const { lang } = useLang();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-6 py-4">
          <Link href="/" className="flex items-center gap-2 text-neutral-500 hover:text-cyan-600 transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm">{lang === "tr" ? "Ana Sayfa" : "Home"}</span>
          </Link>
          <div className="ml-auto">
            <Logo size="sm" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">CCPA Privacy Notice</h1>
          <p className="text-sm text-neutral-400 mb-2">California Consumer Privacy Act Notice</p>
          <p className="text-sm text-neutral-400 mb-8">Last updated: April 7, 2026</p>

          <div className="prose prose-neutral max-w-none text-neutral-600 space-y-6 text-[15px] leading-relaxed">

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">1. Introduction</h2>
              <p>
                This California Consumer Privacy Act (&quot;CCPA&quot;) Privacy Notice is provided by CreateNano LLC, a
                company organized under the laws of the State of New Mexico, USA (&quot;Company,&quot; &quot;we,&quot;
                &quot;us,&quot; or &quot;our&quot;). We operate the SpeakRoar platform (&quot;Platform&quot;), an AI-powered
                voice generation and text-to-speech service.
              </p>
              <p>
                This Notice supplements our general Privacy Policy and applies solely to residents of the State of
                California (&quot;consumers&quot; or &quot;you&quot;). It describes how we collect, use, disclose, and
                otherwise process personal information of California residents, and explains the rights and choices
                available to you under the California Consumer Privacy Act of 2018, as amended by the California Privacy
                Rights Act of 2020 (collectively, &quot;CCPA&quot;).
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">2. Scope</h2>
              <p>
                This Notice applies to personal information we collect from California residents who interact with our
                Platform, including users who create accounts, purchase credits, generate voice content, or otherwise
                engage with our services. It does not apply to information collected from job applicants, employees,
                or business-to-business contacts acting in their professional capacity, except where required by law.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">3. Categories of Personal Information Collected</h2>
              <p>
                In the preceding 12 months, we have collected the following categories of personal information from
                California consumers:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  <strong>Identifiers:</strong> Name, email address, username, unique personal identifier, IP address,
                  account name.
                </li>
                <li>
                  <strong>Commercial Information:</strong> Records of credits purchased, transaction history, voice
                  generation history, and usage records.
                </li>
                <li>
                  <strong>Internet or Other Electronic Network Activity Information:</strong> Browsing history on the
                  Platform, session logs, interactions with the Platform, browser type and version, operating system,
                  and device information.
                </li>
                <li>
                  <strong>Geolocation Data:</strong> Approximate location derived from IP address.
                </li>
                <li>
                  <strong>Audio Information:</strong> AI-generated voice output files created using our service.
                </li>
                <li>
                  <strong>Inferences:</strong> Inferences drawn from the above categories to create a profile reflecting
                  preferences and usage patterns.
                </li>
              </ul>
              <p className="mt-2">
                We do not knowingly collect sensitive personal information as defined under the CCPA/CPRA, such as
                Social Security numbers, driver&apos;s license numbers, financial account credentials, precise
                geolocation, racial or ethnic origin, or biometric information.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">4. Categories of Sources</h2>
              <p>We collect personal information from the following categories of sources:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Directly from you:</strong> Information you provide when creating an account, making a purchase, submitting text for voice generation, or contacting us.</li>
                <li><strong>Automatically from your devices:</strong> Information collected through cookies, analytics tools, and similar technologies when you use our Platform.</li>
                <li><strong>Third-party service providers:</strong> Payment processors, authentication providers, and analytics services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">5. Purposes of Collection and Use</h2>
              <p>We collect and use personal information for the following business and commercial purposes:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Providing, maintaining, and improving the SpeakRoar platform and AI voice generation services</li>
                <li>Processing account registration and managing user accounts</li>
                <li>Processing credit purchases and payment transactions</li>
                <li>Responding to customer support requests and inquiries</li>
                <li>Ensuring the security and integrity of the Platform and preventing fraud or misuse</li>
                <li>Analyzing usage trends and improving service quality</li>
                <li>Complying with legal obligations and enforcing our terms of service</li>
                <li>Communicating with you about your account, transactions, and service updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">6. Sharing and Disclosure of Personal Information</h2>
              <p>
                We may disclose your personal information to the following categories of third parties for business
                purposes:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Cloud and hosting providers:</strong> For platform infrastructure and data storage.</li>
                <li><strong>AI service providers:</strong> For processing text-to-speech requests and generating voice output.</li>
                <li><strong>Payment processors:</strong> For handling credit purchases and payment transactions. We do not directly store your credit card information.</li>
                <li><strong>Analytics providers:</strong> For aggregated usage analytics and platform improvement.</li>
                <li><strong>Legal and regulatory authorities:</strong> When required by law, regulation, or legal process.</li>
              </ul>
              <div className="rounded-xl border border-cyan-100 bg-cyan-50/50 p-4 mt-3">
                <p className="text-sm text-cyan-900">
                  <strong>We do NOT sell personal information.</strong> CreateNano LLC does not sell, and has not in the
                  preceding 12 months sold, the personal information of California consumers. We also do not share
                  personal information for cross-context behavioral advertising purposes.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">7. Your Rights Under the CCPA</h2>
              <p>
                As a California resident, you have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  <strong>Right to Know:</strong> You have the right to request that we disclose the categories and specific
                  pieces of personal information we have collected about you, the categories of sources from which we
                  collected it, the business or commercial purposes for collecting or selling it, and the categories of
                  third parties to whom we disclosed it.
                </li>
                <li>
                  <strong>Right to Delete:</strong> You have the right to request that we delete personal information we
                  have collected from you, subject to certain exceptions provided by law (such as data necessary to
                  complete a transaction, detect security incidents, comply with legal obligations, or exercise free
                  speech).
                </li>
                <li>
                  <strong>Right to Correct:</strong> You have the right to request that we correct inaccurate personal
                  information that we maintain about you.
                </li>
                <li>
                  <strong>Right to Opt-Out of Sale or Sharing:</strong> Although we do not sell your personal information
                  or share it for cross-context behavioral advertising, you have the right to opt out of such practices
                  if they were to occur in the future.
                </li>
                <li>
                  <strong>Right to Limit Use of Sensitive Personal Information:</strong> If we were to collect sensitive
                  personal information, you would have the right to limit its use to purposes necessary to provide the
                  services you request.
                </li>
                <li>
                  <strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of
                  your CCPA rights. We will not deny you goods or services, charge you different prices or rates, provide
                  you with a different level or quality of goods or services, or suggest that you may receive a different
                  price, rate, or level of quality for exercising your rights.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">8. How to Exercise Your Rights</h2>
              <p>
                You may submit a request to exercise your rights by contacting us through any of the following methods:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>
                </li>
                <li>
                  <strong>Account Settings:</strong> You may manage and delete certain data directly through your
                  SpeakRoar account dashboard.
                </li>
              </ul>
              <p className="mt-2">
                You may make a verifiable consumer request up to twice within a 12-month period. We will respond to
                your request within 45 days of receiving it. If we need additional time, we will notify you of the
                extension and the reason for it, for up to an additional 45 days.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">9. Verification Process</h2>
              <p>
                When you submit a request to know or delete personal information, we must verify your identity before
                processing the request. To verify your identity, we may ask you to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Confirm the email address associated with your SpeakRoar account</li>
                <li>Provide information that matches information we already have on file</li>
                <li>Confirm recent account activity or transaction details</li>
              </ul>
              <p className="mt-2">
                If we cannot verify your identity to a sufficient degree of certainty, we may not be able to fulfill your
                request. We will notify you if this is the case and explain the reason.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">10. Authorized Agents</h2>
              <p>
                You may designate an authorized agent to submit a request on your behalf. To do so, you must provide
                the authorized agent with written permission signed by you, and the agent must submit proof of
                authorization along with the request. We may still require you to verify your identity directly with
                us before we process the request.
              </p>
              <p className="mt-2">
                Alternatively, you may authorize an agent using a power of attorney executed pursuant to California
                Probate Code sections 4000 to 4465.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">11. Data Retention</h2>
              <p>
                We retain personal information for as long as necessary to fulfill the purposes for which it was
                collected, including to satisfy legal, accounting, or reporting requirements. Specific retention
                periods include:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Account Data:</strong> Retained while your account is active and for up to 1 year after account deletion.</li>
                <li><strong>Transaction Records:</strong> Retained for up to 7 years as required by applicable tax and commercial laws.</li>
                <li><strong>Voice Generation History:</strong> Retained while your account is active; deleted upon account closure unless subject to legal hold.</li>
                <li><strong>Log and Analytics Data:</strong> Retained for up to 2 years.</li>
                <li><strong>Cookie Data:</strong> Retained according to cookie type, from session duration up to 1 year.</li>
              </ul>
              <p className="mt-2">
                When personal information is no longer necessary for the purposes for which it was collected, we
                securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">12. Children&apos;s Privacy</h2>
              <p>
                SpeakRoar is not directed at children under the age of 16. We do not knowingly collect personal
                information from children under 16. If we learn that we have collected personal information from a
                child under 16, we will take steps to delete that information promptly.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">13. Changes to This Notice</h2>
              <p>
                We reserve the right to update or modify this CCPA Privacy Notice at any time. When we make changes,
                we will update the &quot;Last updated&quot; date at the top of this page. We encourage you to review
                this Notice periodically to stay informed about how we are protecting your personal information.
                Continued use of the Platform after changes are posted constitutes your acceptance of the updated
                Notice.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">14. Contact Information</h2>
              <p>
                If you have any questions or concerns about this CCPA Privacy Notice, or if you wish to exercise your
                rights under the CCPA, please contact us:
              </p>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 mt-3">
                <p className="text-sm text-neutral-800">
                  <strong>CreateNano LLC</strong><br />
                  1209 Mountain Road Pl NE, Ste N<br />
                  Albuquerque, NM 87110, USA<br />
                  Phone: <a href="tel:+15054052773" className="text-cyan-600 hover:underline">(505) 405-2773</a><br />
                  Email:{" "}
                  <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-neutral-400">
          <Link href="/privacy" className="hover:text-cyan-600 transition-colors">Privacy Policy</Link>
          <span>·</span>
          <Link href="/terms" className="hover:text-cyan-600 transition-colors">Terms of Service</Link>
          <span>·</span>
          <Link href="/cookies" className="hover:text-cyan-600 transition-colors">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
